import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { createMutation } from '../lib/mutation';
import { type ReplayDiff, replaySession } from '../lib/sessionReplay';
import { Field } from './Field';
import { Panel } from './Panel';
import { SessionReplayDiff } from './SessionReplayDiff';
import { buttonClass } from './ui/Button';

// "Replay with new policy" (#811, docs/idea/dashboard.md §Audit timeline): a
// sandboxed candidate-policy dry-run over the stored session. READ-ONLY — the
// endpoint executes nothing and writes nothing; an invalid candidate is a 400
// whose reason renders inline, never a thrown view.

export function SessionReplayPanel(props: {
  readonly sessionId: string;
  readonly token: string | null;
}): JSX.Element {
  const [yml, setYml] = createSignal('');
  const [diff, setDiff] = createSignal<ReplayDiff | null>(null);

  // ponytail: a policy-validation 400 answers problem+json with the
  // validator's own errors under `extensions.errors`
  // (`apps/api/src/routes/session-replay.ts:70-78`). The per-error list is
  // currently NOT rendered here — `ApiError` reads `detail`, not `extensions` —
  // so the generic fallback covers the per-error body until apiFetch exposes it.
  const replay = createMutation({
    call: (policy: string) => replaySession(props.sessionId, policy, props.token),
    fallback: t('session_replay.invalid_policy'),
    onSuccess: setDiff,
    onError: () => setDiff(null),
  });
  const busy = replay.pending;
  const error = replay.error;

  const submit = (e: Event): void => {
    e.preventDefault();
    if (yml().trim() === '') return;
    void replay.run(yml());
  };

  return (
    <Panel title={t('audit.replay')} class="session-replay" footer={t('session_replay.hint')}>
      <form class="session-replay__form" onSubmit={submit}>
        <Field label={t('session_replay.yml_label')} id="session-replay-yml">
          {(a11y) => (
            <textarea
              {...a11y}
              class="textarea session-replay__yml"
              rows={10}
              spellcheck={false}
              aria-invalid={error() !== null ? 'true' : undefined}
              value={yml()}
              onInput={(e) => setYml(e.currentTarget.value)}
            />
          )}
        </Field>
        <Show when={error()}>
          <p class="field__error" role="alert">
            {error()}
          </p>
        </Show>
        <button type="submit" class={buttonClass()} disabled={busy() || yml().trim() === ''}>
          {t(busy() ? 'session_replay.running' : 'session_replay.run')}
        </button>
      </form>
      <Show when={diff()} keyed>
        {(d) => <SessionReplayDiff diff={d} />}
      </Show>
    </Panel>
  );
}
